import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  MapPin,
  Save,
  Edit2,
  X,
  MessageCircle,
  Facebook,
  Instagram,
  Linkedin,
  AlertCircle,
  Check,
  Loader2,
  Camera,
  Wallet,
  Globe,
  Lock,
  Eye,
  Clock,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { countries, countriesAndCities } from '@/pet/src/data/countries-cities';
import { useOwnerOnboarding } from '@/pet/src/context/OwnerOnboardingContext';
import { useAuth } from '@/context/AuthContext';
import { apiClient } from '@/api/axios';
import { EmailChangeModal } from '@/pet/src/components/settings/EmailChangeModal';

export const Route = createFileRoute('/dashboard/settings/')({
  component: ProfileSettings,
});

interface UserAddress {
  street?: string;
  city?: string;
  customCity?: string;
  state?: string;
  customState?: string;
  zipCode?: string;
  country?: string;
  customCountry?: string;
}

interface UserSocialMedia {
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  twitter?: string;
}

type PrivacyVisibility = 'private' | 'partial' | 'public';

/** Optional owner type options (multiple selection) */
export const OWNER_TYPE_KEYS = [
  'pet_owner',
  'animal_shelter',
  'animal_rescue',
  'pet_adoption_agency',
  'breeder',
  'kennel',
] as const;
export type OwnerTypeKey = (typeof OWNER_TYPE_KEYS)[number];

interface OwnerDetails {
  name: string;
  email: string;
  imageUrl?: string;
  phone: string;
  whatsapp?: string;
  walletAddress?: string;
  address: UserAddress;
  socialMedia?: UserSocialMedia;
  /** Optional: multiple selection - Pet Owner, Animal Shelter, etc. */
  ownerTypes?: OwnerTypeKey[];
  ownerBasicInfoVisibility: PrivacyVisibility;
  addressVisibility: PrivacyVisibility;
  contactInfoVisibility: PrivacyVisibility;
  familyRelationshipsVisibility?: PrivacyVisibility;
  showLitterSize?: boolean;
}

interface ApiUserProfile {
  id: string;
  clerkId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  imageUrl?: string;
  userType: string;
  phone?: string;
  whatsapp?: string;
  walletAddress?: string;
  address?: UserAddress;
  socialMedia?: UserSocialMedia;
  ownerTypes?: OwnerTypeKey[];
  ownerBasicInfoVisibility?: PrivacyVisibility;
  addressVisibility?: PrivacyVisibility;
  contactInfoVisibility?: PrivacyVisibility;
  familyRelationshipsVisibility?: PrivacyVisibility;
  showLitterSize?: boolean;
}

function ProfileSettings() {
  const { t } = useTranslation();
  const { user, clerkUser } = useAuth();
  const { updateOnboardingSection } = useOwnerOnboarding();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [ownerDetails, setOwnerDetails] = useState<OwnerDetails>({
    name: user?.name || '',
    email: user?.email || '',
    imageUrl: '',
    phone: '',
    whatsapp: '',
    walletAddress: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    },
    socialMedia: {},
    ownerTypes: [],
    ownerBasicInfoVisibility: 'partial',
    addressVisibility: 'partial',
    contactInfoVisibility: 'partial',
    familyRelationshipsVisibility: 'partial',
    showLitterSize: true,
  });

  const [editedDetails, setEditedDetails] =
    useState<OwnerDetails>(ownerDetails);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [showPrivacyPreview, setShowPrivacyPreview] = useState(false);

  // Email change modal + pending state
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [emailChangePending, setEmailChangePending] = useState(false);
  const [pendingNewEmail, setPendingNewEmail] = useState('');
  const [pendingPreviousEmail, setPendingPreviousEmail] = useState('');
  const [verificationDeadline, setVerificationDeadline] = useState('');
  const [emailReverted, setEmailReverted] = useState(false);
  const [revertedFromEmail, setRevertedFromEmail] = useState('');

  const sortedCountries = [...countries].sort((a, b) => a.localeCompare(b));
  sortedCountries.push('Others');

  // Fetch user profile from API on mount
  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get<ApiUserProfile>('/users/profile');
        const profileData = response.data;

        const fetchedDetails: OwnerDetails = {
          name:
            profileData.fullName ||
            `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() ||
            user?.name ||
            '',
          email: profileData.email || user?.email || '',
          // Prefer live Clerk image so UI reflects latest uploaded photo immediately.
          imageUrl: clerkUser?.imageUrl || profileData.imageUrl || '',
          phone: profileData.phone || '',
          whatsapp: profileData.whatsapp || '',
          walletAddress: profileData.walletAddress || '',
          address: profileData.address || {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: '',
          },
          socialMedia: profileData.socialMedia || {},
          ownerTypes: profileData.ownerTypes || [],
          ownerBasicInfoVisibility:
            profileData.ownerBasicInfoVisibility || 'partial',
          addressVisibility: profileData.addressVisibility || 'partial',
          contactInfoVisibility: profileData.contactInfoVisibility || 'partial',
          familyRelationshipsVisibility:
            profileData.familyRelationshipsVisibility ?? 'partial',
          showLitterSize: profileData.showLitterSize ?? true,
        };

        setOwnerDetails(fetchedDetails);
        setEditedDetails(fetchedDetails);

        // Check for pending email change
        try {
          const statusRes = await apiClient.get('/users/email/change-status');
          const statusData = statusRes.data;
          if (statusData.pending) {
            setEmailChangePending(true);
            setPendingNewEmail(statusData.newEmail);
            setPendingPreviousEmail(statusData.previousEmail);
            setVerificationDeadline(statusData.verificationDeadline);
          } else if (statusData.reverted) {
            setEmailReverted(true);
            setRevertedFromEmail(statusData.previousEmail);
            // Refresh displayed email since it was reverted
            setOwnerDetails((prev) => ({
              ...prev,
              email: statusData.previousEmail,
            }));
            setEditedDetails((prev) => ({
              ...prev,
              email: statusData.previousEmail,
            }));
          }
        } catch {
          // Non-critical — status check failed silently
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        // Fall back to user data from context if API fails
        const fallbackDetails: OwnerDetails = {
          name: user?.name || '',
          email: user?.email || '',
          imageUrl: clerkUser?.imageUrl || '',
          phone: '',
          whatsapp: '',
          walletAddress: '',
          address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: '',
          },
          socialMedia: {},
          ownerTypes: [],
          ownerBasicInfoVisibility: 'partial',
          addressVisibility: 'partial',
          contactInfoVisibility: 'partial',
          familyRelationshipsVisibility: 'partial',
          showLitterSize: true,
        };
        setOwnerDetails(fallbackDetails);
        setEditedDetails(fallbackDetails);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchProfile();
    }
  }, [user, clerkUser]);

  useEffect(() => {
    if (
      editedDetails.address.country &&
      editedDetails.address.country !== 'Others'
    ) {
      const states = countriesAndCities[editedDetails.address.country] || [];
      setAvailableStates([...states].sort((a, b) => a.localeCompare(b)));
    } else {
      setAvailableStates([]);
    }
  }, [editedDetails.address.country]);

  useEffect(() => {
    if (
      editedDetails.address.country &&
      editedDetails.address.country !== 'Others'
    ) {
      const cities = countriesAndCities[editedDetails.address.country] || [];
      setAvailableCities([...cities].sort((a, b) => a.localeCompare(b)));
    } else {
      setAvailableCities([]);
    }
  }, [editedDetails.address.country, editedDetails.address.state]);

  const validateMandatoryFields = (): boolean => {
    const errors: string[] = [];
    if (!editedDetails.name.trim())
      errors.push(
        t('settings.profile.validation.nameRequired', {
          defaultValue: 'Name is required',
        })
      );
    if (!editedDetails.email.trim())
      errors.push(
        t('settings.profile.validation.emailRequired', {
          defaultValue: 'Email is required',
        })
      );
    if (!editedDetails.phone.trim())
      errors.push(
        t('settings.profile.validation.contactRequired', {
          defaultValue: 'Contact Number is required',
        })
      );
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSave = async () => {
    if (!validateMandatoryFields()) {
      toast.error(
        t('settings.profile.validation.completeMandatoryFields', {
          defaultValue: 'Please complete all mandatory fields',
        })
      );
      return;
    }

    setIsSaving(true);

    try {
      await apiClient.put('/users/profile', {
        fullName: editedDetails.name,
        phone: editedDetails.phone,
        whatsapp: editedDetails.whatsapp,
        address: editedDetails.address,
        socialMedia: editedDetails.socialMedia,
        ownerTypes: editedDetails.ownerTypes,
        ownerBasicInfoVisibility: editedDetails.ownerBasicInfoVisibility,
        addressVisibility: editedDetails.addressVisibility,
        contactInfoVisibility: editedDetails.contactInfoVisibility,
        familyRelationshipsVisibility:
          editedDetails.familyRelationshipsVisibility,
        showLitterSize: editedDetails.showLitterSize,
      });

      // Sync name update to Clerk so profile photo / display name stays consistent
      if (clerkUser && editedDetails.name.trim()) {
        const parts = editedDetails.name.trim().split(/\s+/);
        const firstName = parts[0] ?? '';
        const lastName = parts.slice(1).join(' ') ?? '';
        try {
          await (
            clerkUser as unknown as {
              update: (v: {
                firstName: string;
                lastName: string;
              }) => Promise<void>;
            }
          ).update({ firstName, lastName });
        } catch {
          // Non-critical — DB name already saved above
        }
      }

      setOwnerDetails(editedDetails);
      setIsEditing(false);
      setValidationErrors([]);

      const basicInfoComplete =
        editedDetails.name.trim() !== '' &&
        editedDetails.email.trim() !== '' &&
        editedDetails.phone.trim() !== '';
      updateOnboardingSection('basicInformation', basicInfoComplete);

      toast.success(t('settings.profile.profileUpdatedSuccess'));
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error(t('settings.profile.profileSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleProfileImageFile = async (file: File) => {
    if (!clerkUser) {
      toast.error(
        t('settings.profile.image.signInAgain', {
          defaultValue: 'Please sign in again to update your profile photo.',
        })
      );
      return;
    }
    setIsImageUploading(true);
    try {
      const result = await (
        clerkUser as unknown as {
          setProfileImage: (args: { file: File }) => Promise<{
            imageUrl?: string;
          }>;
          reload?: () => Promise<void>;
          imageUrl?: string;
        }
      ).setProfileImage({ file });
      if (
        (clerkUser as unknown as { reload?: () => Promise<void> }).reload !=
        null
      ) {
        await (
          clerkUser as unknown as { reload: () => Promise<void> }
        ).reload();
      }
      const nextUrl = result?.imageUrl || clerkUser.imageUrl;
      if (!nextUrl) {
        throw new Error('Profile image URL missing');
      }
      await apiClient.put('/users/profile', { imageUrl: nextUrl });
      // Force backend user sync so social feeds for other users pick up new avatar quickly.
      await apiClient.post('/users/sync', {});
      setOwnerDetails((prev) => ({ ...prev, imageUrl: nextUrl }));
      setEditedDetails((prev) => ({ ...prev, imageUrl: nextUrl }));
      toast.success(
        t('settings.profile.image.updated', {
          defaultValue: 'Profile photo updated.',
        })
      );
    } catch {
      toast.error(
        t('settings.profile.image.uploadFailed', {
          defaultValue: 'Failed to upload profile photo.',
        })
      );
    } finally {
      setIsImageUploading(false);
    }
  };

  useEffect(() => {
    const basicInfoComplete =
      ownerDetails.name.trim() !== '' &&
      ownerDetails.email.trim() !== '' &&
      ownerDetails.phone.trim() !== '';
    updateOnboardingSection('basicInformation', basicInfoComplete);
  }, [ownerDetails, updateOnboardingSection]);

  const handleCancel = () => {
    setEditedDetails(ownerDetails);
    setIsEditing(false);
    setValidationErrors([]);
  };

  const updateAddress = (field: keyof UserAddress, value: string) => {
    const newAddress = { ...editedDetails.address, [field]: value };
    if (field === 'country') {
      newAddress.state = '';
      newAddress.city = '';
      newAddress.customState = '';
      newAddress.customCity = '';
    }
    if (field === 'state') {
      newAddress.city = '';
      newAddress.customCity = '';
    }
    setEditedDetails({ ...editedDetails, address: newAddress });
  };

  const handleCancelEmailChange = async () => {
    try {
      const res = await apiClient.post('/users/email/cancel-change');
      setEmailChangePending(false);
      setPendingNewEmail('');
      setOwnerDetails((prev) => ({ ...prev, email: res.data.email }));
      setEditedDetails((prev) => ({ ...prev, email: res.data.email }));
      toast.success('Email change cancelled.');
    } catch {
      toast.error('Failed to cancel email change.');
    }
  };

  const isMandatoryComplete = () => {
    return ownerDetails.name && ownerDetails.email && ownerDetails.phone;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600 dark:text-white/70">
          {t('common.loading')}
        </span>
      </div>
    );
  }

  const privacyPreviewModal = showPrivacyPreview
    ? createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-preview-title"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowPrivacyPreview(false)}
            aria-hidden="true"
          />
          <div
            className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg border bg-white dark:bg-[#16151D] p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowPrivacyPreview(false)}
              className="absolute right-4 top-4 rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10 dark:text-white/70"
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5" />
            </button>
            <div className="pr-8">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-5 h-5 text-purple-600" />
                <h2
                  id="privacy-preview-title"
                  className="text-lg font-semibold dark:text-white"
                >
                  {t('settings.previewDialog.title')}
                </h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-white/70 mb-4">
                {t('settings.previewDialog.description')}
              </p>
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-[#0F0E16] rounded-lg p-4 border border-gray-200 dark:border-white/10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-full flex items-center justify-center">
                      <span className="text-xl">🐕</span>
                    </div>
                    <div>
                      <h4 className="font-medium dark:text-white">
                        {t('settings.previewDialog.examplePetName')}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-white/60">
                        {t('settings.previewDialog.examplePetBreed')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/10 rounded-lg p-4 border border-purple-200 dark:border-purple-500/20">
                  <h4 className="text-sm font-medium text-purple-800 dark:text-purple-300 mb-3">
                    {t('settings.previewDialog.ownerInformation')}
                  </h4>
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 dark:text-white/50 mb-1">
                      {t('settings.previewDialog.name')}
                    </p>
                    {editedDetails.ownerBasicInfoVisibility === 'private' ? (
                      <p className="text-sm text-gray-600 dark:text-white/70 italic">
                        {t('settings.previewDialog.ownerPrivateMessage')}
                      </p>
                    ) : editedDetails.ownerBasicInfoVisibility === 'partial' ? (
                      <p className="text-sm dark:text-white">
                        {editedDetails.name?.split(' ')[0] ||
                          t('settings.profile.firstName')}
                      </p>
                    ) : (
                      <p className="text-sm dark:text-white">
                        {editedDetails.name || t('settings.profile.fullName')}
                      </p>
                    )}
                  </div>
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 dark:text-white/50 mb-1">
                      {t('settings.previewDialog.location')}
                    </p>
                    {editedDetails.addressVisibility === 'private' ? (
                      <p className="text-sm text-gray-400 dark:text-white/40 italic">
                        {t('settings.previewDialog.notVisible')}
                      </p>
                    ) : editedDetails.addressVisibility === 'partial' ? (
                      <p className="text-sm dark:text-white">
                        {[
                          editedDetails.address?.city,
                          editedDetails.address?.country,
                        ]
                          .filter(Boolean)
                          .join(', ') ||
                          t('settings.previewDialog.cityCountryExample')}
                      </p>
                    ) : (
                      <p className="text-sm dark:text-white">
                        {[
                          editedDetails.address?.street,
                          editedDetails.address?.city,
                          editedDetails.address?.zipCode,
                          editedDetails.address?.country,
                        ]
                          .filter(Boolean)
                          .join(', ') ||
                          t('settings.previewDialog.fullAddressExample')}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-white/50 mb-1">
                      {t('settings.previewDialog.contact')}
                    </p>
                    {editedDetails.contactInfoVisibility === 'private' ? (
                      <p className="text-sm text-gray-400 dark:text-white/40 italic">
                        {t('settings.previewDialog.ownerPrivateMessage')}
                      </p>
                    ) : editedDetails.contactInfoVisibility === 'partial' ? (
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg"
                      >
                        {t('settings.previewDialog.sendSecureMessage')}
                      </button>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-sm dark:text-white">
                          📧{' '}
                          {editedDetails.email ||
                            t('settings.previewDialog.emailExample', {
                              defaultValue: 'email@example.com',
                            })}
                        </p>
                        <p className="text-sm dark:text-white">
                          📞{' '}
                          {editedDetails.phone ||
                            t('settings.previewDialog.phoneExample', {
                              defaultValue: '+1 (555) 123-4567',
                            })}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-white/50 mt-2 italic">
                          {t('settings.previewDialog.sharedForRecovery')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-white/50 space-y-1">
                  <p>{t('settings.previewDialog.currentSettings')}</p>
                  <ul className="list-disc list-inside pl-2">
                    <li>
                      {t('settings.previewDialog.nameLabel')}{' '}
                      <span className="capitalize">
                        {editedDetails.ownerBasicInfoVisibility}
                      </span>
                    </li>
                    <li>
                      {t('settings.previewDialog.addressLabel')}{' '}
                      <span className="capitalize">
                        {editedDetails.addressVisibility}
                      </span>
                    </li>
                    <li>
                      {t('settings.previewDialog.contactLabel')}{' '}
                      <span className="capitalize">
                        {editedDetails.contactInfoVisibility}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="space-y-6">
      {privacyPreviewModal}
      <EmailChangeModal
        isOpen={showEmailChangeModal}
        onClose={() => setShowEmailChangeModal(false)}
        currentEmail={pendingPreviousEmail || ownerDetails.email}
        pendingEmail={emailChangePending ? pendingNewEmail : undefined}
        onPending={(newEmail, deadline) => {
          setEmailChangePending(true);
          setPendingNewEmail(newEmail);
          setPendingPreviousEmail(ownerDetails.email);
          setVerificationDeadline(deadline);
          setOwnerDetails((prev) => ({ ...prev, email: newEmail }));
          setEditedDetails((prev) => ({ ...prev, email: newEmail }));
        }}
        onVerified={(newEmail) => {
          setEmailChangePending(false);
          setPendingNewEmail('');
          setPendingPreviousEmail('');
          setVerificationDeadline('');
          setOwnerDetails((prev) => ({ ...prev, email: newEmail }));
          setEditedDetails((prev) => ({ ...prev, email: newEmail }));
        }}
      />
      {/* Edit/Save Actions */}
      <div className="flex justify-end gap-2">
        {isEditing ? (
          <>
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 text-gray-700 dark:text-white/90 disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isSaving
                ? t('common.saving', 'Saving...')
                : t('settings.profile.saveChanges')}
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:opacity-90"
          >
            <Edit2 className="w-4 h-4" />
            {t('common.edit')} {t('settings.tabs.profile')}
          </button>
        )}
      </div>

      {/* Mandatory Completion Alert */}
      {!isMandatoryComplete() && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-orange-900 dark:text-orange-300 mb-1">
                {t('onboarding.banner.title')}
              </h3>
              <p className="text-sm text-orange-800 dark:text-orange-200">
                {t('settings.profile.onboardingRequired')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-medium text-red-900 dark:text-red-300 mb-2">
                {t('settings.profile.fixErrors')}
              </h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-800 dark:text-red-200">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Basic Information - MANDATORY */}
      <div className="bg-white dark:bg-[#16151D] border-2 border-blue-200 dark:border-blue-500/30 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            <h3 className="text-xl dark:text-white">
              {t('onboarding.banner.basicInformation')}
            </h3>
            <span className="ml-2 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded">
              {t('common.required')}
            </span>
          </div>
        </div>

        <div className="mb-5">
          <label className="block text-sm mb-2 dark:text-white/90">
            {t('settings.profile.ownerPhoto', { defaultValue: 'Owner Photo' })}{' '}
            <span className="text-gray-500 dark:text-white/50 text-xs">
              ({t('common.optional')})
            </span>
          </label>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border border-gray-200 dark:border-white/10 bg-[#F0EFF5] dark:bg-white/10 flex items-center justify-center">
              {editedDetails.imageUrl ? (
                <img
                  src={editedDetails.imageUrl}
                  alt={t('settings.profile.ownerProfilePhotoAlt', {
                    defaultValue: 'Owner profile',
                  })}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-8 h-8 text-[#936FEF]" />
              )}
            </div>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleProfileImageFile(file);
                    e.currentTarget.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isImageUploading}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-white/20 hover:bg-gray-50 dark:hover:bg-white/5 text-sm disabled:opacity-60"
                >
                  {isImageUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                  {t('settings.profile.uploadImage', {
                    defaultValue: 'Upload image',
                  })}
                </button>
                {clerkUser?.imageUrl && (
                  <span className="text-xs text-gray-500 dark:text-white/50">
                    {t('settings.profile.image.googleClerkDefault', {
                      defaultValue: 'Google/Clerk photo is used by default.',
                    })}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-white/70">
                {editedDetails.imageUrl
                  ? t('settings.profile.image.photoAdded', {
                      defaultValue: 'Photo added. Used in Social posts.',
                    })
                  : t('settings.profile.image.noPhoto', {
                      defaultValue: 'No photo added yet.',
                    })}
              </p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-2 dark:text-white/90">
              {t('settings.profile.fullName')}{' '}
              <span className="text-red-600">*</span>
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editedDetails.name}
                onChange={(e) =>
                  setEditedDetails({ ...editedDetails, name: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none"
                placeholder={t('settings.profile.enterFullName')}
              />
            ) : (
              <p className="text-gray-900 dark:text-white/95">
                {ownerDetails.name || t('common.notProvided')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-2 dark:text-white/90">
              {t('settings.profile.emailAddress')}{' '}
              <span className="text-red-600">*</span>
            </label>
            <div className="flex items-center gap-3">
              <p className="text-gray-900 dark:text-white/95 flex-1">
                {ownerDetails.email || t('common.notProvided')}
              </p>
              <button
                type="button"
                onClick={() => setShowEmailChangeModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 border border-purple-300 dark:border-purple-500/30 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20"
              >
                <Mail className="w-3.5 h-3.5" />
                Change Email
              </button>
            </div>
            {/* Pending verification banner */}
            {emailChangePending && (
              <div className="mt-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      Pending verification for{' '}
                      <strong>{pendingNewEmail}</strong>.
                      {verificationDeadline && (
                        <>
                          {' '}
                          Verify by{' '}
                          {new Date(verificationDeadline).toLocaleString()} or
                          it will revert.
                        </>
                      )}
                    </p>
                    <div className="flex gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => setShowEmailChangeModal(true)}
                        className="text-xs font-medium text-purple-600 dark:text-purple-400 hover:underline"
                      >
                        Verify Now
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEmailChange}
                        className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
                      >
                        Cancel Change
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Email reverted banner */}
            {emailReverted && (
              <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      Your email change was not verified in time and has been
                      reverted to <strong>{revertedFromEmail}</strong>.
                    </p>
                    <button
                      type="button"
                      onClick={() => setEmailReverted(false)}
                      className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline mt-1"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm mb-2 dark:text-white/90">
              {t('settings.profile.contactNumber')}{' '}
              <span className="text-red-600">*</span>
            </label>
            {isEditing ? (
              <input
                type="tel"
                value={editedDetails.phone}
                onChange={(e) =>
                  setEditedDetails({ ...editedDetails, phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none"
                placeholder="+1 (555) 123-4567"
              />
            ) : (
              <p className="text-gray-900 dark:text-white/95">
                {ownerDetails.phone || t('common.notProvided')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-2 dark:text-white/90">
              {t('settings.profile.whatsapp')}{' '}
              <span className="text-gray-500 dark:text-white/50 text-xs">
                ({t('common.optional')})
              </span>
            </label>
            {isEditing ? (
              <input
                type="tel"
                value={editedDetails.whatsapp || ''}
                onChange={(e) =>
                  setEditedDetails({
                    ...editedDetails,
                    whatsapp: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none"
                placeholder="+1 (555) 123-4567"
              />
            ) : (
              <p className="text-gray-900 dark:text-white/95">
                {ownerDetails.whatsapp || t('common.notProvided')}
              </p>
            )}
          </div>

          {/* Owner type – optional multiple selection */}
          <div className="md:col-span-2">
            <label className="block text-sm mb-2 dark:text-white/90">
              {t('settings.profile.ownerTypeLabel', 'I am a')}{' '}
              <span className="text-gray-500 dark:text-white/50 text-xs">
                ({t('common.optional')})
              </span>
            </label>
            {isEditing ? (
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {OWNER_TYPE_KEYS.map((key) => {
                  const checked = (editedDetails.ownerTypes || []).includes(
                    key
                  );
                  return (
                    <label
                      key={key}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const current = editedDetails.ownerTypes || [];
                          const next = checked
                            ? current.filter((k) => k !== key)
                            : [...current, key];
                          setEditedDetails({
                            ...editedDetails,
                            ownerTypes: next,
                          });
                        }}
                        className="w-4 h-4 rounded border-gray-300 dark:border-white/20 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm dark:text-white/90">
                        {t(`settings.profile.ownerType.${key}`)}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-900 dark:text-white/95 text-sm">
                {(ownerDetails.ownerTypes?.length ?? 0) > 0
                  ? (ownerDetails.ownerTypes || [])
                      .map((k) => t(`settings.profile.ownerType.${k}`))
                      .join(', ')
                  : t('common.none')}
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm mb-2 dark:text-white/90">
              <span className="flex items-center gap-1.5">
                <Wallet className="w-4 h-4 text-purple-600" />
                {t('settings.profile.walletAddress')}{' '}
                <span className="text-gray-500 dark:text-white/50 text-xs">
                  ({t('settings.profile.managedInWallet')})
                </span>
              </span>
            </label>
            <p className="text-gray-900 dark:text-white/95 font-mono text-sm break-all">
              {ownerDetails.walletAddress || t('common.notConnected')}
            </p>
          </div>
        </div>
      </div>

      {/* Address - OPTIONAL */}
      <div className="bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/8 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-5 h-5 text-blue-600" />
          <h3 className="text-xl dark:text-white">
            {t('settings.profile.address')}
          </h3>
          <span className="text-sm text-gray-500 dark:text-white/50 ml-2">
            ({t('common.optional')})
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm mb-2 dark:text-white/90">
              {t('settings.profile.streetAddress')}
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editedDetails.address.street || ''}
                onChange={(e) => updateAddress('street', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none"
                placeholder={t('settings.profile.enterStreetAddress')}
              />
            ) : (
              <p className="text-gray-900 dark:text-white/95">
                {ownerDetails.address.street || t('common.notProvided')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-2 dark:text-white/90">
              {t('settings.profile.country')}
            </label>
            {isEditing ? (
              <>
                <select
                  value={editedDetails.address.country || ''}
                  onChange={(e) => updateAddress('country', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none"
                >
                  <option value="">
                    {t('settings.profile.selectCountry')}
                  </option>
                  {sortedCountries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
                {editedDetails.address.country === 'Others' && (
                  <input
                    type="text"
                    value={editedDetails.address.customCountry || ''}
                    onChange={(e) =>
                      updateAddress('customCountry', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none mt-2"
                    placeholder={t('settings.profile.enterCountry')}
                  />
                )}
              </>
            ) : (
              <p className="text-gray-900 dark:text-white/95">
                {ownerDetails.address.country === 'Others'
                  ? ownerDetails.address.customCountry ||
                    t('common.notProvided')
                  : ownerDetails.address.country || t('common.notProvided')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-2 dark:text-white/90">
              {t('settings.profile.state')}
            </label>
            {isEditing ? (
              <>
                <select
                  value={editedDetails.address.state || ''}
                  onChange={(e) => updateAddress('state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none disabled:bg-gray-100"
                  disabled={
                    !editedDetails.address.country ||
                    editedDetails.address.country === 'Others'
                  }
                >
                  <option value="">
                    {!editedDetails.address.country ||
                    editedDetails.address.country === 'Others'
                      ? t('settings.profile.selectCountryFirst')
                      : t('settings.profile.selectState')}
                  </option>
                  {availableStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                  {editedDetails.address.country &&
                    editedDetails.address.country !== 'Others' && (
                      <option value="Others">
                        {t('settings.profile.others')}
                      </option>
                    )}
                </select>
                {editedDetails.address.state === 'Others' && (
                  <input
                    type="text"
                    value={editedDetails.address.customState || ''}
                    onChange={(e) =>
                      updateAddress('customState', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none mt-2"
                    placeholder={t('settings.profile.enterState')}
                  />
                )}
              </>
            ) : (
              <p className="text-gray-900 dark:text-white/95">
                {ownerDetails.address.state === 'Others'
                  ? ownerDetails.address.customState || t('common.notProvided')
                  : ownerDetails.address.state || t('common.notProvided')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-2 dark:text-white/90">
              {t('settings.profile.city')}
            </label>
            {isEditing ? (
              <>
                <select
                  value={editedDetails.address.city || ''}
                  onChange={(e) => updateAddress('city', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none disabled:bg-gray-100"
                  disabled={
                    !editedDetails.address.country ||
                    editedDetails.address.country === 'Others'
                  }
                >
                  <option value="">
                    {!editedDetails.address.country ||
                    editedDetails.address.country === 'Others'
                      ? t('settings.profile.selectCountryFirst')
                      : t('settings.profile.selectCity')}
                  </option>
                  {availableCities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                  {editedDetails.address.country &&
                    editedDetails.address.country !== 'Others' && (
                      <option value="Others">
                        {t('settings.profile.others')}
                      </option>
                    )}
                </select>
                {editedDetails.address.city === 'Others' && (
                  <input
                    type="text"
                    value={editedDetails.address.customCity || ''}
                    onChange={(e) =>
                      updateAddress('customCity', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none mt-2"
                    placeholder={t('settings.profile.enterCity')}
                  />
                )}
              </>
            ) : (
              <p className="text-gray-900 dark:text-white/95">
                {ownerDetails.address.city === 'Others'
                  ? ownerDetails.address.customCity || t('common.notProvided')
                  : ownerDetails.address.city || t('common.notProvided')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm mb-2 dark:text-white/90">
              {t('settings.profile.postalCode')}
            </label>
            {isEditing ? (
              <input
                type="text"
                value={editedDetails.address.zipCode || ''}
                onChange={(e) => updateAddress('zipCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none"
                placeholder={t('settings.profile.postalCodePlaceholder')}
              />
            ) : (
              <p className="text-gray-900 dark:text-white/95">
                {ownerDetails.address.zipCode || t('common.notProvided')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Social Media - OPTIONAL */}
      <div className="bg-white dark:bg-[#16151D] border border-gray-200 dark:border-white/8 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="w-5 h-5 text-blue-600" />
          <h3 className="text-xl dark:text-white">
            {t('settings.profile.socialMedia')}
          </h3>
          <span className="text-sm text-gray-500 dark:text-white/50 ml-2">
            ({t('common.optional')})
          </span>
        </div>
        <p className="text-gray-600 dark:text-white/70 mb-6">
          {t('settings.profile.socialMediaDesc')}
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Facebook */}
          <div className="p-4 border border-gray-200 dark:border-white/8 rounded-lg hover:border-blue-300 dark:hover:border-blue-500/30 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Facebook className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <div className="font-medium dark:text-white">Facebook</div>
                <div className="text-xs text-gray-500 dark:text-white/50">
                  {t('settings.profile.personalProfile')}
                </div>
              </div>
              {ownerDetails.socialMedia?.facebook && (
                <Check className="w-5 h-5 text-green-600" />
              )}
            </div>
            {isEditing ? (
              <input
                type="url"
                value={editedDetails.socialMedia?.facebook || ''}
                onChange={(e) =>
                  setEditedDetails({
                    ...editedDetails,
                    socialMedia: {
                      ...editedDetails.socialMedia,
                      facebook: e.target.value,
                    },
                  })
                }
                placeholder="https://facebook.com/yourprofile"
                className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none text-sm"
              />
            ) : ownerDetails.socialMedia?.facebook ? (
              <p className="text-sm text-blue-600 truncate">
                {ownerDetails.socialMedia.facebook}
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-white/50">
                {t('common.notConnected')}
              </p>
            )}
          </div>

          {/* Instagram */}
          <div className="p-4 border border-gray-200 dark:border-white/8 rounded-lg hover:border-purple-300 dark:hover:border-purple-500/30 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-lg flex items-center justify-center">
                <Instagram className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <div className="font-medium dark:text-white">Instagram</div>
                <div className="text-xs text-gray-500 dark:text-white/50">
                  {t('settings.profile.personalProfile')}
                </div>
              </div>
              {ownerDetails.socialMedia?.instagram && (
                <Check className="w-5 h-5 text-green-600" />
              )}
            </div>
            {isEditing ? (
              <input
                type="url"
                value={editedDetails.socialMedia?.instagram || ''}
                onChange={(e) =>
                  setEditedDetails({
                    ...editedDetails,
                    socialMedia: {
                      ...editedDetails.socialMedia,
                      instagram: e.target.value,
                    },
                  })
                }
                placeholder="https://instagram.com/yourprofile"
                className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none text-sm"
              />
            ) : ownerDetails.socialMedia?.instagram ? (
              <p className="text-sm text-purple-600 truncate">
                {ownerDetails.socialMedia.instagram}
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-white/50">
                {t('common.notConnected')}
              </p>
            )}
          </div>

          {/* LinkedIn */}
          <div className="p-4 border border-gray-200 dark:border-white/8 rounded-lg hover:border-blue-600 dark:hover:border-blue-500/30 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Linkedin className="w-5 h-5 text-blue-700 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <div className="font-medium dark:text-white">LinkedIn</div>
                <div className="text-xs text-gray-500 dark:text-white/50">
                  {t('settings.profile.professionalProfile')}
                </div>
              </div>
              {ownerDetails.socialMedia?.linkedin && (
                <Check className="w-5 h-5 text-green-600" />
              )}
            </div>
            {isEditing ? (
              <input
                type="url"
                value={editedDetails.socialMedia?.linkedin || ''}
                onChange={(e) =>
                  setEditedDetails({
                    ...editedDetails,
                    socialMedia: {
                      ...editedDetails.socialMedia,
                      linkedin: e.target.value,
                    },
                  })
                }
                placeholder="https://linkedin.com/in/yourprofile"
                className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none text-sm"
              />
            ) : ownerDetails.socialMedia?.linkedin ? (
              <p className="text-sm text-blue-700 truncate">
                {ownerDetails.socialMedia.linkedin}
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-white/50">
                {t('common.notConnected')}
              </p>
            )}
          </div>

          {/* Twitter */}
          <div className="p-4 border border-gray-200 dark:border-white/8 rounded-lg hover:border-sky-500 dark:hover:border-sky-500/30 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-sky-50 dark:bg-sky-900/20 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-sky-600 dark:text-sky-400" />
              </div>
              <div className="flex-1">
                <div className="font-medium dark:text-white">Twitter / X</div>
                <div className="text-xs text-gray-500 dark:text-white/50">
                  {t('settings.profile.socialProfile')}
                </div>
              </div>
              {ownerDetails.socialMedia?.twitter && (
                <Check className="w-5 h-5 text-green-600" />
              )}
            </div>
            {isEditing ? (
              <input
                type="url"
                value={editedDetails.socialMedia?.twitter || ''}
                onChange={(e) =>
                  setEditedDetails({
                    ...editedDetails,
                    socialMedia: {
                      ...editedDetails.socialMedia,
                      twitter: e.target.value,
                    },
                  })
                }
                placeholder="https://twitter.com/yourhandle"
                className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 dark:bg-[#0F0E16] dark:text-white rounded-lg focus:ring-2 focus:ring-blue-600 outline-none text-sm"
              />
            ) : ownerDetails.socialMedia?.twitter ? (
              <p className="text-sm text-sky-600 truncate">
                {ownerDetails.socialMedia.twitter}
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-white/50">
                {t('common.notConnected')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Global Search Privacy */}
      <div className="bg-white dark:bg-[#16151D] border-2 border-purple-200 dark:border-purple-500/30 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-purple-600" />
            <Lock className="w-4 h-4 text-purple-600" />
            <h3 className="text-xl dark:text-white">
              {t('settings.privacy.globalSearchPrivacy')}
            </h3>
            <span className="ml-2 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded font-medium">
              {t('settings.privacy.recommended')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowPrivacyPreview(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 border border-purple-300 dark:border-purple-500/30 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20"
          >
            <Eye className="w-4 h-4" />
            {t('settings.privacy.preview')}
          </button>
        </div>
        <p className="text-gray-600 dark:text-white/70 mb-6">
          {t('settings.privacy.privacyDescription')}
        </p>

        {/* Owner Basic Information Visibility */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-white/80 mb-3">
            {t('settings.privacy.ownerBasicInfoVisibility')}
          </h4>
          <div className="space-y-2">
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                editedDetails.ownerBasicInfoVisibility === 'private'
                  ? 'border-2 border-purple-500 dark:border-purple-400 bg-purple-50/50 dark:bg-purple-900/10'
                  : 'border border-gray-200 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/30'
              }`}
            >
              <input
                type="radio"
                name="ownerBasicInfoVisibility"
                value="private"
                checked={editedDetails.ownerBasicInfoVisibility === 'private'}
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    ownerBasicInfoVisibility: 'private',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                  <span className="font-medium dark:text-white">
                    {t('settings.privacy.private')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.privateOwnerDesc')}
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                editedDetails.ownerBasicInfoVisibility === 'partial'
                  ? 'border-2 border-purple-500 dark:border-purple-400 bg-purple-50/50 dark:bg-purple-900/10'
                  : 'border border-gray-200 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/30'
              }`}
            >
              <input
                type="radio"
                name="ownerBasicInfoVisibility"
                value="partial"
                checked={editedDetails.ownerBasicInfoVisibility === 'partial'}
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    ownerBasicInfoVisibility: 'partial',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-purple-500" />
                  <span className="font-medium dark:text-white">
                    {t('settings.privacy.partialPrivacy')}
                  </span>
                  <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs rounded">
                    {t('settings.privacy.recommended')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.partialOwnerDesc')}
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                editedDetails.ownerBasicInfoVisibility === 'public'
                  ? 'border-2 border-purple-500 dark:border-purple-400 bg-purple-50/50 dark:bg-purple-900/10'
                  : 'border border-gray-200 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/30'
              }`}
            >
              <input
                type="radio"
                name="ownerBasicInfoVisibility"
                value="public"
                checked={editedDetails.ownerBasicInfoVisibility === 'public'}
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    ownerBasicInfoVisibility: 'public',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-green-500" />
                  <span className="font-medium dark:text-white">
                    {t('settings.privacy.publicContact')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.publicOwnerDesc')}
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Address Visibility */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-white/80 mb-3">
            {t('settings.privacy.addressVisibility')}
          </h4>
          <div className="space-y-2">
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                editedDetails.addressVisibility === 'private'
                  ? 'border-2 border-purple-500 dark:border-purple-400 bg-purple-50/50 dark:bg-purple-900/10'
                  : 'border border-gray-200 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/30'
              }`}
            >
              <input
                type="radio"
                name="addressVisibility"
                value="private"
                checked={editedDetails.addressVisibility === 'private'}
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    addressVisibility: 'private',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                  <span className="font-medium dark:text-white">
                    {t('settings.privacy.private')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.privateAddressDesc')}
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                editedDetails.addressVisibility === 'partial'
                  ? 'border-2 border-purple-500 dark:border-purple-400 bg-purple-50/50 dark:bg-purple-900/10'
                  : 'border border-gray-200 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/30'
              }`}
            >
              <input
                type="radio"
                name="addressVisibility"
                value="partial"
                checked={editedDetails.addressVisibility === 'partial'}
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    addressVisibility: 'partial',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-purple-500" />
                  <span className="font-medium dark:text-white">
                    {t('settings.privacy.partialPrivacy')}
                  </span>
                  <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs rounded">
                    {t('settings.privacy.recommended')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.partialAddressDesc')}
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                editedDetails.addressVisibility === 'public'
                  ? 'border-2 border-purple-500 dark:border-purple-400 bg-purple-50/50 dark:bg-purple-900/10'
                  : 'border border-gray-200 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/30'
              }`}
            >
              <input
                type="radio"
                name="addressVisibility"
                value="public"
                checked={editedDetails.addressVisibility === 'public'}
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    addressVisibility: 'public',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-green-500" />
                  <span className="font-medium dark:text-white">
                    {t('settings.privacy.publicContact')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.publicAddressDesc')}
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Contact Information Visibility */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-white/80 mb-3">
            {t('settings.privacy.contactInfoVisibility')}
          </h4>
          <div className="space-y-2">
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                editedDetails.contactInfoVisibility === 'private'
                  ? 'border-2 border-purple-500 dark:border-purple-400 bg-purple-50/50 dark:bg-purple-900/10'
                  : 'border border-gray-200 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/30'
              }`}
            >
              <input
                type="radio"
                name="contactInfoVisibility"
                value="private"
                checked={editedDetails.contactInfoVisibility === 'private'}
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    contactInfoVisibility: 'private',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                  <span className="font-medium dark:text-white">
                    {t('settings.privacy.private')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.privateContactDesc')}
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                editedDetails.contactInfoVisibility === 'partial'
                  ? 'border-2 border-purple-500 dark:border-purple-400 bg-purple-50/50 dark:bg-purple-900/10'
                  : 'border border-gray-200 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/30'
              }`}
            >
              <input
                type="radio"
                name="contactInfoVisibility"
                value="partial"
                checked={editedDetails.contactInfoVisibility === 'partial'}
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    contactInfoVisibility: 'partial',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-purple-500" />
                  <span className="font-medium dark:text-white">
                    {t('settings.privacy.partialPrivacy')}
                  </span>
                  <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs rounded">
                    {t('settings.privacy.recommended')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.partialContactDesc')}
                </p>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                editedDetails.contactInfoVisibility === 'public'
                  ? 'border-2 border-purple-500 dark:border-purple-400 bg-purple-50/50 dark:bg-purple-900/10'
                  : 'border border-gray-200 dark:border-white/10 hover:border-purple-300 dark:hover:border-purple-500/30'
              }`}
            >
              <input
                type="radio"
                name="contactInfoVisibility"
                value="public"
                checked={editedDetails.contactInfoVisibility === 'public'}
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    contactInfoVisibility: 'public',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-green-500" />
                  <span className="font-medium dark:text-white">
                    {t('settings.privacy.publicContact')}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.publicContactDesc')}
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Family & Litter Privacy */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-white/80 mb-3">
            {t('settings.privacy.familyLitterTitle')}
          </h4>
          <p className="text-sm text-gray-500 dark:text-white/60 mb-3">
            {t('settings.privacy.familyLitterDesc')}
          </p>
          <div className="space-y-2 mb-4">
            <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all border border-gray-200 dark:border-white/10">
              <input
                type="radio"
                name="familyRelationshipsVisibility"
                value="private"
                checked={
                  (editedDetails.familyRelationshipsVisibility ?? 'partial') ===
                  'private'
                }
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    familyRelationshipsVisibility: 'private',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <span className="font-medium dark:text-white">
                  {t('settings.privacy.private')}
                </span>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.familyPrivateDesc')}
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all border border-gray-200 dark:border-white/10">
              <input
                type="radio"
                name="familyRelationshipsVisibility"
                value="partial"
                checked={
                  (editedDetails.familyRelationshipsVisibility ?? 'partial') ===
                  'partial'
                }
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    familyRelationshipsVisibility: 'partial',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <span className="font-medium dark:text-white">
                  {t('settings.privacy.partialLabel')}
                </span>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.familyPartialDesc')}
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all border border-gray-200 dark:border-white/10">
              <input
                type="radio"
                name="familyRelationshipsVisibility"
                value="public"
                checked={
                  (editedDetails.familyRelationshipsVisibility ?? 'partial') ===
                  'public'
                }
                onChange={() =>
                  setEditedDetails({
                    ...editedDetails,
                    familyRelationshipsVisibility: 'public',
                  })
                }
                className="mt-1"
                disabled={!isEditing}
              />
              <div>
                <span className="font-medium dark:text-white">
                  {t('settings.privacy.publicLabel')}
                </span>
                <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                  {t('settings.privacy.familyPublicDesc')}
                </p>
              </div>
            </label>
          </div>
          <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-white/10 cursor-pointer">
            <input
              type="checkbox"
              checked={editedDetails.showLitterSize ?? true}
              onChange={(e) =>
                setEditedDetails({
                  ...editedDetails,
                  showLitterSize: e.target.checked,
                })
              }
              disabled={!isEditing}
              className="rounded"
            />
            <div>
              <span className="font-medium dark:text-white">
                {t('settings.privacy.showLitterSize')}
              </span>
              <p className="text-sm text-gray-500 dark:text-white/60 mt-1">
                {t('settings.privacy.showLitterSizeDesc')}
              </p>
            </div>
          </label>
        </div>

        {/* How it works info */}
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/20 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {t('settings.privacy.howItWorks')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
